<template>
  <el-dialog
    id="edit-modal"
    class="form-modal"
    :visible.sync="editVisible"
    v-loading="editProcessing || ttsProcessing"
    :fullscreen="true"
    :show-close="false"
    :modal="false"
  >
    <div id="edit-modal-header" class="modal-header">
      <div class="modal-header-layout flex grid-6 align-center">
        <h1 class="modal-title col-2">View / Edit Mediafile</h1>
        <div class="modal-actions col-2">
          <el-button type="primary" @click="editOperation">Save</el-button>
          <el-button type="default" @click="cancelOperation"> Cancel</el-button>
        </div>
      </div>
    </div>

    <el-form>
      <recent-audits service="Media File" :record="selectedMediafile" />
      <el-input placeholder="Enter name" v-model="mediafileName">
        <template slot="prefix">Name</template>
      </el-input>

      <h2 class="form-section-header">Upload or record</h2>
      <div class="mt-20 mb-10">
        <div class="inline-form-group">
          <audio id="mediafile" style="display: none" src=""></audio>

          <div class="mediafile-controls-flex flex align-center">
            <el-button
              v-loading="mediafilePlayPending"
              class="mediafile-play-button mr-5"
              :disabled="!!!mediafileBlob && !mediafileExists"
              :icon="mediafileIsPlaying ? 'mdi mdi-pause' : 'mdi mdi-play'"
              @click="mediafileIsPlaying ? pauseFile() : playFile()"
              circle
              type="primary"
            >
            </el-button>
            <el-tooltip
              placement="top"
              content="Press and hold to record"
              class="flex align-center justify-center"
            >
              <div>
                <VueRecordAudio
                  ref="vueRecorderMediafile"
                  mode="hold"
                  @stream="gotStreamMediafile"
                  @result="onDoneRecordingMediafile"
                />
              </div>
            </el-tooltip>
            <el-tooltip placement="top" content="Upload file">
              <el-upload
                class="ml-5 mr-5"
                action="hailFred"
                accept="audio/*"
                :auto-upload="false"
                :on-change="showUploadedMediafile"
                :show-file-list="false"
              >
                <el-button class="mr-0" circle icon="el-icon-upload2">
                </el-button>
              </el-upload>
            </el-tooltip>
            <el-tooltip
              placement="top"
              content="Download current"
              v-if="mediafileExists"
            >
              <el-button
                v-loading="mediafileDownloadPending"
                circle
                icon="el-icon-download"
                @click="downloadMediafile"
              >
              </el-button>
            </el-tooltip>
            <div class="mediafile-save-actions ml-30">
              <el-tooltip
                placement="top"
                content="Cancel changes"
                v-if="mediafileChanged"
              >
                <el-button
                  type="danger"
                  circle
                  icon="mdi mdi-cancel"
                  @click="cancelChanges"
                >
                </el-button>
              </el-tooltip>
            </div>
          </div>
        </div>
      </div>

      <text-to-speech
        :clearFields="clearFormFields"
        :media-file-text="mediaFileText"
        @processingStart="ttsProcessing = true"
        @processingEnd="ttsProcessing = false"
        @processingComplete="onTTSComplete"
      />
    </el-form>

    <destination-references
      :tenantId="selectedMediafile ? selectedMediafile.tenantId : null"
      type="PLAYBACK"
      :id="selectedMediafile ? selectedMediafile.id : null"
      :clearState="clearFormFields"
    />
  </el-dialog>
</template>

<script>
import feathersClient from "@/feathers/feathers-client";
import DestinationReferences from "@/components/DestinationReferences";
import RecentAudits from "@/components/RecentAudits";
import TextToSpeech from "./textToSpeech.vue";

export default {
  name: "MediafilesEditForm",

  props: {
    editVisible: { required: true },
    clearFormFields: { required: true },
    editProcessing: { required: true },
    selectedMediafile: { required: true }
  },

  components: {
    DestinationReferences,
    RecentAudits,
    TextToSpeech
  },

  data() {
    return {
      mediafileIsPlaying: false,
      mediafileAudioElement: null,
      mediafileBlob: null,
      mediafileName: null,
      mediafileChanged: false,
      mediafileExists: !!this.selectedMediafile,
      mediafileRecord: this.selectedMediafile,
      mediafileDownloadPending: false,
      mediafilePlayPending: false,
      ttsProcessing: false,
      mediaFileText: "",
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    }
  },

  watch: {
    clearFormFields(value) {
      if (value) {
        this.clearFields();
      }
    },

    selectedMediafile(val) {
      this.mediafileRecord = val;
      this.mediafileExists = !!val;

      if (val) {
        this.mediafileName = val.name;
        this.mediaFileText = val.text;
        this.lastMediaText = val.text;
      }
    }
  },

  methods: {
    onTTSComplete(data) {
      const { blob, text } = data;
      this.mediafileBlob = blob;
      this.lastMediaText = text;

      this.mediafileAudioElement.src = window.URL.createObjectURL(
        this.mediafileBlob
      );
      this.mediafileChanged = true;

      this.$notify({
        type: "success",
        title: "Success",
        message: "The preview is ready for playback."
      });
    },

    normalizeFileName(name) {
      // Normalize to NFD (decomposed form) and remove diacritical marks
      return name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    },

    sanitizeText(text) {
      if (!text) return text;
      const map = {
        "\u2018": "'", "\u2019": "'", "\u201a": "'", "\u201b": "'",
        "\u201c": '"', "\u201d": '"', "\u201e": '"', "\u201f": '"',
        "\u2032": "'", "\u2033": '"',
        "\u2010": "-", "\u2011": "-", "\u2012": "-", "\u2013": "-",
        "\u2014": "-", "\u2015": "-", "\u2212": "-",
        " ": " ", " ": " ", " ": " ", " ": " ",
        "\u200b": "", "\u00ad": "",
        "\u2026": "...", "\u2022": "*",
      };
      return text.replace(
        /[\u2018-\u201f\u2032\u2033\u2010-\u2015\u2212    \u200b\u00ad\u2026\u2022]/g,
        ch => (map[ch] !== undefined ? map[ch] : ch)
      );
    },

    cancelOperation() {
      this.pauseFile();
      this.$emit("cancelOperation", "edit");
    },

    gotStreamMediafile(data) {
      this.mediafileBlob = null;
    },

    onDoneRecordingMediafile(data) {
      this.mediafileBlob = data;
      this.mediafileAudioElement.src = window.URL.createObjectURL(data);
      this.mediafileChanged = true;
    },

    async playFile() {
      if (
        this.mediafileExists &&
        !this.mediafileBlob &&
        !this.mediafileChanged
      ) {
        // ⭐️ Fetch s3 recording, set blob and play ⭐️ //
        const mediafile = await this.fetchMediafile();

        if (mediafile) {
          this.mediafileIsPlaying = true;
          this.mediafileAudioElement.play();
        } else {
          console.log("Failed to fetch the file.");
        }
        this.mediafileIsPlaying = true;
      } else {
        this.mediafileIsPlaying = true;
        this.mediafileAudioElement.play();
      }
    },

    pauseFile() {
      this.mediafileIsPlaying = false;
      this.mediafileAudioElement.pause();
      this.mediafileAudioElement.currentTime = 0;
    },

    cancelChanges() {
      this.mediafileBlob = null;
      this.mediafileChanged = false;
    },

    showUploadedMediafile(file) {
      this.mediafileBlob = new Blob([file.raw]);
      this.mediafileAudioElement.src = window.URL.createObjectURL(
        this.mediafileBlob
      );
      this.mediafileChanged = true;
    },

    async fetchMediafile() {
      this.setPlayPending();
      return await feathersClient
        .service("mediafiles")
        .get(this.mediafileRecord.id)
        .then((res) => {
          if (!res.recording) {
            this.$notify({
              title: "Not Found",
              message: "File Not Found.",
              type: "warning"
            });
            this.mediafilePlayPending = false;
          } else {
            this.setAudioBlob(res.recording);
            this.setPlayPending();
          }
          return true;
        })
        .catch(() => {
          this.setPlayPending();
        });
    },

    editOperation() {
      if (!this.mediafileName) {
        this.$notify({
          type: "error",
          title: "File name is empty",
          message: "You must input the mediafile name.",
        });
        return;
      }

      let formData = new FormData();
      formData.append("name", this.normalizeFileName(this.mediafileName));
      formData.append("tenantId", this.selectedTenant.id);
      formData.append("text", this.sanitizeText(this.lastMediaText));

      const mediafile = this.mediafileBlob;
      if (mediafile) {
        formData.append("mediaFile", mediafile);
      }

      this.$emit("editOperation", formData);
    },

    downloadMediafile() {
      this.setDownloadPending();
      feathersClient
        .service("mediafiles")
        .get(this.mediafileRecord.id)
        .then((res) => {
          const link = document.createElement("a");
          link.href = res.recording;
          link.click();
          this.setDownloadPending();
        })
        .catch(() => true);
    },

    setAudioBlob(url) {
      this.mediafileBlob = url;
      this.mediafileAudioElement.src = this.mediafileBlob;
    },

    setDownloadPending() {
      this.mediafileDownloadPending = !this.mediafileDownloadPending;
    },

    setPlayPending() {
      this.mediafilePlayPending = !this.mediafilePlayPending;
    },

    clearFields() {
      this.mediafileIsPlaying = false;
      this.doneRecording = null;
      this.mediafileBlob = null;
      this.mediafileName = null;
      this.mediafileChanged = false;
      this.mediafileExists = false;
      this.mediafileDownloadPending = false;
      this.mediafilePlayPending = false;
    }
  },

  mounted() {
    this.mediafileAudioElement = document.querySelector("audio");

    if (this.mediafileAudioElement) {
      this.mediafileAudioElement.addEventListener("ended", () => {
        this.mediafileIsPlaying = false;
      });
    }
  },

  beforeDestroy() {
    if (this.mediafileAudioElement) {
      this.mediafileAudioElement.removeEventListener("ended", () => true);
    }
  }
};
</script>

<style lang="scss" scoped>
.el-form {
  max-width: 600px;
  margin: 0 auto;
}

.el-checkbox {
  margin-left: 0;
}

.vue-audio-recorder {
  width: 40px !important;
  height: 40px !important;
  background-color: #ef5350 !important;
}

.mediafile-play-button {
  width: 55px;
  height: 55px;
  i {
    font-size: 2em;
  }
}

hr {
  opacity: 0.25;
}
</style>
